/**
 * What a save OWES the trainer because of the structure they changed — and the reason a save can
 * never ask twice.
 *
 * Editing the STRUCTURE of a running plan can move the client's dates and can move the trainer's own
 * bookings, and those are two different questions with two different answers. Three of them live
 * here, each keyed to a different field:
 *
 *   1. **WHAT structurally changed?**  → `pendingStructuralEdits`, from the TREE and its load-time
 *      baseline alone. Mode-agnostic: no scheduling mode, no execution mode.
 *   2. **Does that RE-DATE the client?** → `phaseWindowRedatingReason`, and it is a question about
 *      PHASE BOUNDARIES rather than about days. A day is placed by its `dayOfWeek`, so its
 *      neighbours cannot push it; a cycle whose PHASE changed is re-dated onto the new phase's
 *      weekdays, and nothing measures that.
 *   3. **Does it touch the trainer's OWN BOOKINGS?** → an EXECUTION-mode question
 *      (`draft.live.executionMode`). `affectsStationaryBookings`, and it holds whether or not any
 *      date moves: a stationary occurrence carries a trainer id and a time slot, so restructuring
 *      moves or frees a reservation regardless.
 *
 * 2 and 3 are NOT fused and must not be. A stationary plan re-dates nothing when days are reordered
 * but still books the trainer; a remote plan whose first phase was shortened re-dates a cycle and
 * books nobody. They compose at `structuralSaveRoute`, which is the ONE place allowed to ask both,
 * and which returns a single route precisely so that one save can never raise two confirmations.
 *
 * ── The measured route is GONE, with the engine behind it ─────────────────────────────────────
 * A third route, `preview`, used to spend a round trip on `POST /plan-template/{id}/structural-edit-
 * preview` to count the occurrences a SequenceBased retile would move. The backend deleted the
 * sequence engine and that endpoint with it, so there is no longer anything on either side that can
 * MEASURE a re-dating. Every consent this module raises is now taken without a count, which is what
 * `confirm-dates` already did and why its copy names no number.
 *
 * ── `addDayGate` is GONE, deliberately, and this is the record of why ─────────────────────────
 * Mobile's gate refused a day added to any but the LAST phase of a running tape fork, on the
 * reasoning that a tape phase's length is its days walked in order accumulating `1 + restDaysAfter`
 * — so an extra day early on lengthened that phase's cycle and pushed every later occurrence down
 * the calendar. That is the rest-day date arithmetic `self-paced-plan-requirements.md` R2 forbids,
 * and R18 states the consequence directly: because no occurrence after the cursor has a date,
 * "adding a training day to one cycle cannot move another occurrence's date, so the gesture's blast
 * radius is confined to the cycle the trainer clicked". The gate's whole premise is deleted with the
 * engine it described, so the gate is deleted rather than left refusing an edit that is now free.
 *
 * What still refuses a day is `dayAdditionRefusal` in `draftOperations`: the phase's fourteen-day cap,
 * and a running fork whose every cycle of the phase is closed to new days. The second is the rule
 * mobile's `addDayGate` asks today (`phaseFullyLived`). Neither reads a rest day.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/structuralEditFlow.ts` ─────────
 * Verbatim, with one adaptation: **`serverStructureOf` is inlined.** Mobile keeps it as a named
 * function to tolerate a crash-recovery draft record written before `serverStructure` existed. This
 * app persists no draft at all (ADR §6), so the only producer of a draft is `responseToDraft`, which
 * always writes the field.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { DayDraft, PlanDraft, ServerDayWindow } from './planDraft.types';

/**
 * A structural change to a running plan's tree.
 *
 * `deletePhase`, `resizePhase` and `addPhase` are the PHASE-LENGTH edits, and whether they re-date
 * anything depends on WHERE they happen: at the tail they add or remove occurrences and leave every
 * existing one alone, while in the middle they slide every later phase's window and the cycles that
 * changed phase land on the new phase's weekdays. `shiftsStoredPhaseWindows` is what tells the two
 * apart, because the kind cannot — a duplicate reports itself as `addPhase` wherever it lands.
 *
 * The six others re-date nothing. A day is placed by its `dayOfWeek`, so adding, deleting,
 * reordering or re-gapping days around it cannot push it; `moveDayWeekday` moves one day's
 * occurrences to a weekday the trainer chose, which is the move rather than a consequence of it;
 * and `dayWindowChange` withdraws or mints occurrences without moving a single date.
 *
 * All nine are still DETECTED, because every one of them frees, moves or creates an occurrence — and
 * on a stationary assignment every occurrence is a reservation the trainer must be told about. That
 * is the `warn` route, and it is why `affectsStationaryBookings` considers the whole set while
 * `phaseWindowRedatingReason` weighs only three of them, by position.
 */
export type StructuralEditKind =
  | 'addDay'
  | 'deleteDay'
  | 'reorderDay'
  | 'moveDayWeekday'
  | 'dayWindowChange'
  | 'restDaysChange'
  | 'deletePhase'
  | 'resizePhase'
  | 'addPhase';

/**
 * The edits that can re-date a running plan — the three PHASE-LENGTH kinds, and only when they moved
 * a later phase's window. See `shiftsStoredPhaseWindows` for why the kind alone cannot decide.
 */
export type RedatingEditKind = 'deletePhase' | 'resizePhase' | 'addPhase';

/**
 * The order the kinds are reported, and therefore which one names the dialog when several are
 * pending: the most consequential first. Adding or deleting a day changes a cycle's length for good;
 * a reorder only re-slots within it; a rest-gap tweak is the smallest of the four. Phase-length edits
 * come last — they never win a naming contest against a day edit, and they can only ever reach the
 * shared booking copy anyway.
 */
const KIND_ORDER: readonly StructuralEditKind[] = [
  'addDay',
  'deleteDay',
  'reorderDay',
  'moveDayWeekday',
  'dayWindowChange',
  'restDaysChange',
  'deletePhase',
  'resizePhase',
  'addPhase',
];

/**
 * The PHASE-length edits — the three that re-date a running plan **only when they move a later
 * phase's window.**
 *
 * They used to be excluded outright, on a reason that holds for exactly one of the shapes they take:
 * a phase APPENDED at the tail, or the LAST phase made longer or shorter, creates or removes
 * occurrences at the end and moves nothing that already exists. The same three kinds in the MIDDLE
 * of a plan do the opposite — `WorkoutExecutionPropagator.ResolveTarget` cannot answer a cycle whose
 * phase changed by identity, so it falls through to matching on `d.Order` in the phase that now owns
 * it and `RedateToNewWeekday` puts the occurrence on THAT day's weekday. On a REMOTE fork that
 * reached the client with no consent at all: `affectsStationaryBookings` catches the stationary case
 * and nothing caught this one.
 *
 * `addPhase` is the kind a DUPLICATE reports itself as, which is why the kind alone can never decide
 * — the copy lands directly after its source, which is the middle of the plan far more often than
 * the end.
 */
const PHASE_WINDOW_REASONS: ReadonlySet<StructuralEditKind> = new Set<StructuralEditKind>([
  'deletePhase',
  'resizePhase',
  'addPhase',
]);

/**
 * Has any STORED phase's window moved along the cycle axis since the plan was loaded?
 *
 * The one question that separates a tail edit from a middle one, asked as a COORDINATE rather than
 * as a kind: `rebasePhaseWindows` restamps every phase's `startCycle` after each structural edit, so
 * a stored phase whose current start differs from its captured one is a phase whose whole block of
 * occurrences has slid — and that is precisely the set the retiler re-dates.
 *
 * It answers false for the three cases that must stay on `save-direct`: a phase appended at the
 * tail (nothing follows it), the last phase lengthened or shortened (its own start never moves), and
 * the last phase deleted. It answers true for a copy inserted mid-plan, for an earlier phase
 * resized, and for a middle phase removed.
 *
 * A phase the trainer created in this session has no captured start and is skipped: it carries no
 * occurrence that could move, and the phases it PUSHED are the stored ones this walk is about.
 */
function shiftsStoredPhaseWindows(draft: PlanDraft): boolean {
  const baseline = draft.live.serverStructure;
  if (baseline === null) return false;
  return draft.phases.some((phase) => {
    if (phase.serverId == null) return false;
    const loaded = baseline.phases[phase.serverId];
    return loaded !== undefined && loaded.startCycle !== phase.startCycle;
  });
}

/**
 * A NEW day that has become serialisable. An empty new day is DROPPED by `draftToRequest`, so it is
 * not yet an addition as far as the server is concerned — and a preview taken over it would come
 * back "nothing moved", teaching the trainer that adding a day is free.
 */
function hasAddedDay(draft: PlanDraft): boolean {
  return draft.phases.some((phase) =>
    phase.days.some((day) => day.serverId === null && day.exercises.length > 0),
  );
}

/**
 * A NEW phase — appended or duplicated in this session — that has become serialisable.
 *
 * The same rule and the same reason as `hasAddedDay` one level up: `phaseToInput` REFUSES a phase
 * whose every day is exercise-less, so an empty new phase is not an addition the server will ever
 * see, and reporting one would put a consent dialog in front of an edit that changes nothing.
 *
 * It is its own kind rather than folded into `resizePhase`, because the two are different acts with
 * the same consequence: a phase appended at the tail creates occurrences the client did not have,
 * and on a stationary assignment every one of them is a reservation the trainer must be told about.
 * `resizePhase` cannot stand in for it — a new phase has no baseline row to differ from.
 */
function hasAddedPhase(draft: PlanDraft): boolean {
  return draft.phases.some(
    (phase) =>
      phase.serverId === null && phase.days.some((day) => day.exercises.length > 0),
  );
}

/**
 * Stored days no longer in the order the server gave them — some day's captured `serverOrder` is out
 * of ascending sequence with its current array position.
 *
 * Detected that way rather than tracked as a flag because a reorder deliberately PRESERVES
 * `slotToken` and `serverId` — that is exactly what makes it safe — so the loaded position is the
 * only witness left. A day without a `serverOrder` was added or duplicated this session and has no
 * position to have moved from.
 */
function hasReorderedDay(draft: PlanDraft): boolean {
  return draft.phases.some((phase) => {
    let previous = -Infinity;
    for (const day of phase.days) {
      if (day.serverOrder == null) continue;
      if (day.serverOrder < previous) return true;
      previous = day.serverOrder;
    }
    return false;
  });
}

/**
 * Does this day now run in different weeks than the ones it was loaded with?
 *
 * Compared field by field on the AUTHORED window rather than on the resolved cycle list, because
 * that is what the save sends and what the backend rebuilds from — and because resolving would need
 * the phase span, which this walk deliberately does not carry. The holes are compared as a SET: the
 * wire's list is unordered and both sides keep it sorted, so a difference in order would be this
 * app's bug rather than an edit.
 *
 * ── The comparison is RELATIVE to the phase, and it has to be ────────────────────────────────
 * The coordinates are plan-absolute, so `rebasePhaseWindows` slides every window of every phase
 * that moved along the cycle axis — that is the whole reason that pass exists. Compared in absolute
 * terms, lengthening the first phase of a plan would report a window change on every day of every
 * later phase, and the trainer would be told they had changed which weeks a day runs in when they
 * had changed nothing of the sort. `delta` is the phase's own shift, so what this measures is
 * whether the day moved WITHIN its phase. A bound the rebase had to clamp did move within it, and
 * is correctly reported.
 */
function hasMovedWindow(loaded: ServerDayWindow, day: DayDraft, delta: number): boolean {
  const shift = (cycle: number | null): number | null => (cycle === null ? null : cycle + delta);
  if (shift(loaded.activationCycle) !== day.activationCycle) return true;
  if (shift(loaded.retirementCycle) !== day.retirementCycle) return true;
  if (loaded.excludedCycles.length !== day.excludedCycles.length) return true;
  const before = new Set(loaded.excludedCycles.map((cycle) => cycle + delta));
  return day.excludedCycles.some((cycle) => !before.has(cycle));
}

/**
 * Which structural edits the draft is carrying, judged from the TREE and its load-time baseline
 * alone — no scheduling mode, no execution mode. Empty for a non-fork draft: a library entry has no
 * client schedule to disturb and no baseline to measure against.
 *
 * Deletions and value changes are answered by walking `live.serverStructure`, because a deleted node
 * leaves nothing in the tree to carry its own provenance. A day that vanished because its whole
 * PHASE was deleted reports `deletePhase` only, never `deleteDay` — the baseline nests days under
 * their phase precisely so the two stay distinguishable, and conflating them would send every phase
 * deletion on a sequence plan through a preview round trip that can only come back "nothing moved".
 */
export function pendingStructuralEdits(draft: PlanDraft): StructuralEditKind[] {
  if (!draft.live.isForkTemplate) return [];

  const found = new Set<StructuralEditKind>();
  if (hasAddedDay(draft)) found.add('addDay');
  if (hasReorderedDay(draft)) found.add('reorderDay');
  if (hasAddedPhase(draft)) found.add('addPhase');

  const baseline = draft.live.serverStructure;
  if (baseline) {
    const survivingPhases = new Set<number>();
    for (const phase of draft.phases) {
      if (phase.serverId == null) continue;
      const loaded = baseline.phases[phase.serverId];
      if (loaded === undefined) continue;
      survivingPhases.add(phase.serverId);
      if (loaded.cycles !== phase.cycles) found.add('resizePhase');

      const survivingDays = new Set<number>();
      for (const day of phase.days) {
        if (day.serverId == null) continue;
        survivingDays.add(day.serverId);
        // `undefined` means the day was not in this phase's baseline at all; a stored `null` is a day
        // the server gave no gap for, which differs from a gap of 0 and must still register.
        const loadedRest = loaded.dayRestDays[day.serverId];
        if (loadedRest !== undefined && loadedRest !== day.restDaysAfter) found.add('restDaysChange');
        // The weekly counterpart, added with PR-E's session drag — see `ServerPhaseStructure`.
        // `hasReorderedDay` cannot stand in for it: a one-day phase has no order to disturb and a
        // Monday moved to Tuesday keeps its position, yet both move every not-yet-started
        // occurrence of that day and, on a stationary assignment, the booking on each of them.
        const loadedWeekday = loaded.dayWeekdays[day.serverId];
        if (loadedWeekday !== undefined && loadedWeekday !== day.dayOfWeek) {
          found.add('moveDayWeekday');
        }
        // The per-cycle lifespan, added with the calendar's gestures. Nothing else in this walk can
        // see it: a window edit keeps the day's id, its token, its position and its weekday, so the
        // captured window is the only witness there is.
        const loadedWindow = loaded.dayWindows[day.serverId];
        if (
          loadedWindow !== undefined &&
          hasMovedWindow(loadedWindow, day, phase.startCycle - loaded.startCycle)
        ) {
          found.add('dayWindowChange');
        }
      }
      if (Object.keys(loaded.dayRestDays).some((id) => !survivingDays.has(Number(id)))) {
        found.add('deleteDay');
      }
    }
    if (Object.keys(baseline.phases).some((id) => !survivingPhases.has(Number(id)))) {
      found.add('deletePhase');
    }
  }

  return KIND_ORDER.filter((kind) => found.has(kind));
}

/**
 * Is a RUNNING plan carrying any structural edit at all, whatever its execution mode?
 *
 * The seam the booking warning hangs off. Do not narrow it to the re-dating kinds, and do not fold
 * the booking question into `phaseWindowRedatingReason`: the two answer different questions about
 * different people.
 */
export function hasPendingStructuralEdit(draft: PlanDraft): boolean {
  return pendingStructuralEdits(draft).length > 0;
}

/**
 * The phase-window edit a running fork owes a consent for — the one nothing can measure.
 *
 * "A day is placed by its `dayOfWeek`, so its neighbours cannot push it" is true of DAY edits and
 * **false of phase boundaries**, and that distinction was missed. A cycle whose PHASE changed is the
 * one case `WorkoutExecutionPropagator.ResolveTarget` cannot answer by identity — its day belongs to
 * the phase it left — so it falls through to matching on `d.Order` in the phase that now owns it,
 * and `RedateToNewWeekday` puts the occurrence on THAT day's weekday. Shorten an early phase by one
 * cycle and the client's Monday/Wednesday cycle becomes a Tuesday/Thursday/Saturday one, keeping its
 * row and its booking and changing every date in it.
 *
 * **The consent is taken with no count, and there is no longer an endpoint that could supply one.**
 * `POST /plan-template/{id}/structural-edit-preview` measured a SequenceBased retile and went with
 * the sequence engine, so the copy names the consequence in words rather than a number.
 */
function phaseWindowRedatingReason(draft: PlanDraft): RedatingEditKind | null {
  // A self-paced plan has NO date to re-date. R8: no occurrence that has not been lived carries a
  // dated row anywhere, so sliding a phase along the cycle axis moves no client's calendar — R23
  // says so of the closest gesture there is, that adding a cycle to a running self-paced plan
  // "moves nothing and requires no consent about dates". A consent dialog here would ask the
  // trainer to agree to a consequence that cannot happen.
  if (draft.mode === 'selfpaced') return null;
  if (!shiftsStoredPhaseWindows(draft)) return null;
  return (
    pendingStructuralEdits(draft).find((kind): kind is RedatingEditKind =>
      PHASE_WINDOW_REASONS.has(kind),
    ) ?? null
  );
}

/**
 * Does this save move, free or create the TRAINER'S own reservations?
 *
 * True only when the client trains in person: a stationary occurrence carries a trainer id and a time
 * slot, so any restructuring touches one. Remote and elastic assignments book nothing.
 *
 * The backend never loses a reservation — a trainer-booked occurrence is detached to an empty booked
 * slot rather than deleted — so this is a "tell them", never a "stop them".
 */
export function affectsStationaryBookings(draft: PlanDraft): boolean {
  // A self-paced assignment is REMOTE by contract (R11): stationary is not expressible — it needs a
  // booked slot per weekday and this plan has neither weekdays nor future dates to book — and the
  // backend refuses one. So there is no reservation for a restructure to move, whatever
  // `executionMode` happens to say, and asking the execution mode on this branch would make a stale
  // or unrecognised value able to raise a warning about bookings that cannot exist.
  if (draft.mode === 'selfpaced') return false;
  return draft.live.executionMode === 'stationary' && hasPendingStructuralEdit(draft);
}

/** Which facts a consent dialog must state — see `structuralSaveRoute` for why this is one value. */
export type StructuralConsentScope = 'dates' | 'datesAndBookings' | 'bookings';

/**
 * What a save owes the trainer, as ONE value.
 *
 * · `save-direct`   — nothing structural pending, or nothing either axis cares about. Commit.
 * · `confirm-dates` — a phase boundary moved, so a cycle changed phase and its occurrences land on
 *                     the new phase's weekdays. Consent is owed and there is **nothing that can
 *                     measure it**, so it is taken with no round trip and no count. See
 *                     `phaseWindowRedatingReason`.
 * · `warn`          — the dates do not move but the trainer's bookings do: the stationary case. No
 *                     server round trip; there is nothing to measure and no endpoint to ask.
 *
 * The two axes compose HERE, into one route, and that is the whole point: a trainer editing a
 * stationary plan gets ONE dialog stating both facts, never a calendar confirmation followed by a
 * booking confirmation. `warnsBookings` rides on the dates route rather than being a second gate for
 * exactly that reason.
 *
 * **A SELF-PACED plan always answers `save-direct`, and it does so through TWO INDEPENDENT GUARDS —
 * one in each axis — rather than through a branch in `structuralSaveRoute`.** Both
 * `phaseWindowRedatingReason` and `affectsStationaryBookings` open with an explicit
 * `draft.mode === 'selfpaced'` early return, each on its own reason: there is no dated occurrence to
 * re-date (R8), and no bookable one to move (R11, remote only). An earlier revision of this note
 * said the mode was never named at all and that the two axes merely came out empty; that was wrong,
 * and the distinction matters — a third axis added later inherits nothing, and must state its own
 * answer for the mode the same way these two do.
 */
export type StructuralSaveRoute =
  | { readonly route: 'save-direct' }
  | { readonly route: 'warn' }
  | {
      readonly route: 'confirm-dates';
      readonly reason: RedatingEditKind;
      readonly warnsBookings: boolean;
    };

export function structuralSaveRoute(draft: PlanDraft): StructuralSaveRoute {
  const warnsBookings = affectsStationaryBookings(draft);
  const redating = phaseWindowRedatingReason(draft);
  if (redating !== null) return { route: 'confirm-dates', reason: redating, warnsBookings };
  return warnsBookings ? { route: 'warn' } : { route: 'save-direct' };
}

/** Everything a consent dialog needs. */
export interface StructuralConsentRequest {
  readonly scope: StructuralConsentScope;
  /** The cause the dates copy names. Null exactly when `scope` is `'bookings'` — that one is causeless. */
  readonly reason: RedatingEditKind | null;
}

/** What the save does next — the whole remaining decision. */
export type StructuralSaveStep =
  | { readonly step: 'commit' }
  | ({ readonly step: 'consent' } & StructuralConsentRequest);

/**
 * Resolve the route into one next move.
 *
 * A pure function rather than branches in a hook, so the invariant the trainer actually feels is
 * testable: **a save resolves to `commit` or to exactly ONE `consent`, never to a sequence of them.**
 * There is no shape of this return value that asks twice.
 *
 * Nothing is measured on any route. A consent never degrades to a commit, because there is no answer
 * that could tell it the edit was free — the absence of a count is that there is nothing to count
 * with, not a zero.
 */
export function structuralSaveStep(route: StructuralSaveRoute): StructuralSaveStep {
  if (route.route === 'save-direct') return { step: 'commit' };
  if (route.route === 'warn') {
    return { step: 'consent', scope: 'bookings', reason: null };
  }
  return {
    step: 'consent',
    scope: route.warnsBookings ? 'datesAndBookings' : 'dates',
    reason: route.reason,
  };
}
